<template>
    <div>
        <page-header title-tag="h3">
            {{ greeting }}
            <template #side>
                <ButtonIcon
                    class="mr-2"
                    icon="refresh-cw"
                    variant="link"
                    :disabled="loading"
                    :icon-animation="iconAnimation"
                    @click="reload"
                />
                <TimeRangeCalendar
                    v-model="timeRange"
                    :disabled="loading"
                    :auto-refresh="true"
                    :options="['today', 'yesterday', 'last7Days', 'currentWeek', 'last14Days', 'last30days', 'currentMonth', 'currentQuarter', 'currentYear']"
                />
            </template>
        </page-header>

        <div class="row">
            <div class="col-xl-3 col-md-6 mb-4">
                <WidgetStatChart
                    :title="$t('dashboard.impressions')"
                    :value="$format.number(impressions.impressions)"
                    :difference="$format.percent(percentDiff(impressions.impressions, impressions.impressions_compare))"
                    :data="getWidgetChartData('impressions')"
                    :loading="requestImpressionsStats.getLoading()"
                    :error="requestImpressionsStats.getError()"
                    :chart-color="chartOverview.impressions.color"
                    tooltip-formatter="round"
                />
            </div>
            <div class="col-xl-3 col-md-6 mb-4">
                <WidgetStatChart
                    :title="$t('dashboard.clicks')"
                    :value="$format.number(clicks.clicks)"
                    :difference="$format.percent(percentDiff(clicks.clicks, clicks.clicks_compare))"
                    :data="getWidgetChartData('clicks')"
                    :loading="requestClicksStats.getLoading()"
                    :error="requestClicksStats.getError()"
                    :chart-color="chartOverview.clicks.color"
                    tooltip-formatter="round"
                />
            </div>
            <div class="col-xl-3 col-md-6 mb-4">
                <WidgetStatChart
                    :title="$t('dashboard.transactions')"
                    :value="$format.number(orders.orders)"
                    :difference="$format.percent(percentDiff(orders.orders, orders.orders_compare))"
                    :data="getWidgetChartData('orders')"
                    :loading="requestOrdersStats.getLoading()"
                    :error="requestOrdersStats.getError()"
                    :chart-color="chartOverview.orders.color"
                    tooltip-formatter="round"
                />
            </div>
            <div class="col-xl-3 col-md-6 mb-4">
                <WidgetStatChart
                    :title="$t('dashboard.commission')"
                    :value="$format.currency(commission.publisher_commission)"
                    :difference="$format.percent(percentDiff(commission.publisher_commission, commission.publisher_commission_compare))"
                    :data="getWidgetChartData('commission')"
                    :loading="requestCommissionStats.getLoading()"
                    :error="requestCommissionStats.getError()"
                    :chart-color="chartOverview.commission.color"
                    tooltip-formatter="currency"
                />
            </div>
        </div>

        <div class="row">
            <div class="col-xl-3 mb-4">
                <TipboxCommission ref="tipboxCommission" />
            </div>
            <div class="col-xl-9 mb-4">
                <MultiEChartCard
                    :data="chartOverview"
                    :loading="isChartOverviewLoading"
                    :title="$t('dashboard.overview')"
                    type="time"
                    @currentChart="setCurrentChart"
                />
            </div>
        </div>

        <div class="row">
            <div class="col-xl-5 mb-4">
                <WidgetNewCampaigns ref="widgetNewCampaigns" />
            </div>
            <div class="col-xl-7 mb-4">
                <WidgetRecentTransactions ref="widgetRecentTransactions" />
            </div>
        </div>

        <div class="row">
            <div class="col-xl-4 mb-4">
                <WidgetBestSpaces ref="widgetBestSpaces" :time-range="timeRange || {}" />
            </div>
            <div class="col-xl-4 mb-4">
                <WidgetBestCampaigns ref="widgetBestCampaigns" :time-range="timeRange || {}" />
            </div>
            <div class="col-xl-4 mb-4">
                <WidgetBestProducts ref="widgetBestProducts" :time-range="timeRange || {}" />
            </div>
        </div>
    </div>
</template>

<script>
    import DashboardMixin from '@/views/common/dashboard/index.vue';

    export default {
        name: 'WidgeterDashboard',
        mixins: [DashboardMixin],
        data() {
            return {
                requestCommissions: this.$req('finance/commissions'),
            };
        },
        computed: {
            user() {
                return this.$store.getters['auth/getUser'];
            },
            greeting() {
                // eslint-disable-next-line no-unused-vars
                const locale = this.$i18n.locale;
                const firstName = this.user?.firstName;
                const nameSuffix = firstName ? `, ${firstName}` : '';
                return this.$t('dashboard.greeting', { name: nameSuffix });
            },
            spaceCount() {
                return this.$store.getters['space/spaceCount'];
            },
            loading() {
                return (
                    this.requestImpressionsStats.getLoading() ||
                    this.requestClicksStats.getLoading() ||
                    this.requestOrdersStats.getLoading() ||
                    this.requestCommissionStats.getLoading() ||
                    this.requestCommissions.getLoading() ||
                    this.requestCampaigns.getLoading() ||
                    this.requestUserTransactions.getLoading() ||
                    this.requestSpaceReport.getLoading() ||
                    this.requestCampaignReport.getLoading() ||
                    this.requestTopProductsQuantity.getLoading() ||
                    this.isChartOverviewLoading
                );
            },
            iconAnimation() {
                return this.loading ? 'spin' : undefined;
            },
            chartOverview() {
                return {
                    impressions: this.getChartData(this.impressions.chart, 'impressions', this.$t('dashboard.impressions'), '#5369f8'),
                    clicks: this.getChartData(this.clicks.chart, 'clicks', this.$t('dashboard.clicks'), '#f77e53'),
                    orders: this.getChartData(this.orders.chart, 'orders', this.$t('dashboard.transactions'), '#43d39e'),
                    commission: this.getChartData(this.commission.chart, 'publisher_commission', this.$t('dashboard.commission'), '#ffbe0b', 'currency'),
                    amount: this.getChartData(this.amount.chart, 'amount', this.$t('dashboard.salesValue'), '#890070', 'currency'),
                };
            },
        },
        methods: {
            fetchData(params = {}) {
                if (!this.spaceCount) return;
                this.fetchImpressionsStats(params);
                this.fetchClicksStats(params);
                this.fetchOrdersStats(params);
                this.fetchCommissionStats(params);
                this.fetchAmountStats(params);
            },
            reload() {
                const params = { cache: false };
                this.fetchData(params);
                this.$refs.tipboxCommission.fetchData(params);
                this.$refs.widgetNewCampaigns.fetchData(params);
                this.$refs.widgetRecentTransactions.fetchData(params);
                this.$refs.widgetBestSpaces.fetchData(params);
                this.$refs.widgetBestCampaigns.fetchData(params);
                this.$refs.widgetBestProducts.fetchData(params);
            },
        },
    };
</script>
